import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { clearActiveMission, readActiveMission, setActiveMission } from '../../utils/activeMission';
import { createChatOutbox } from '../../utils/chat/outbox';
import Conversation from '../chat/Conversation';
import CollectionCard from './CollectionCard';
import EventBoard from './EventBoard';
import EventFindAi from './EventFindAi';
import FieldMiniMap from './FieldMiniMap';
import IsolationPanel from './IsolationPanel';
import PrepareForEvent from './PrepareForEvent';
import RadiusScan from './RadiusScan';
import { can } from '../../utils/permissions';
import { useEventLive } from './EventLiveProvider';

/*
 * A live field event in Field Mode (/field/event/:missionId; Part III
 * section 19): the full map (me, my team, my next waypoint and its search
 * ring), the leaderboards, the event's chat and the finds' cards, with the
 * camera one tap away. For staff and for guests alike -- a guest reaches
 * Field Mode only for an event it was let into (App.js, Field.js).
 *
 * Opening it makes the event the active mission (when the person takes part
 * and it is not over), so the camera stamps every find with it and the
 * heartbeat (EventLiveProvider) runs.
 */
const TABS = ['map', 'board', 'chat', 'finds'];
const NOT_FOUND = 'event_not_found';

export default function EventField({ userId, user }) {
  const { t } = useTranslation('events');
  const { missionId } = useParams();
  const navigate = useNavigate();
  const live = useEventLive();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('map');
  const [cards, setCards] = useState([]);
  const [zones, setZones] = useState([]);
  const outbox = useMemo(() => createChatOutbox(), []);
  const guest = (user?.roles || []).includes('public');

  // No `t` in the dependencies: without a stable identity it would load forever.
  const load = useCallback(() => api.get(`/events/${missionId}`)
    .then((r) => { setEvent(r.data); setError(''); })
    .catch((e) => setError(e?.response?.status === 404 ? NOT_FOUND : errorText(e))), [missionId]);
  const loadCards = useCallback(() => api.get(`/events/${missionId}/cards`)
    .then((r) => setCards(r.data.items || [])).catch(() => {}), [missionId]);

  useEffect(() => { load(); loadCards(); }, [load, loadCards]);
  // Fire-safe zones, while field isolation is on for the event (PR11).
  const isolationOn = !!(live?.live?.isolation?.enabled ?? event?.isolation_enabled);
  useEffect(() => {
    if (!isolationOn) { setZones([]); return; }
    api.get(`/events/${missionId}/fire-zones`).then((r) => setZones(r.data.items || [])).catch(() => {});
  }, [isolationOn, missionId]);

  // Out on this event: the camera stamps finds with it, the heartbeat runs.
  // Once it is over, it stops being the active mission (a guest's Field Mode
  // goes with it).
  useEffect(() => {
    if (event && ['finished', 'cancelled'].includes(event.phase)) {
      clearActiveMission(undefined, event.mission_id);
      return;
    }
    if (!event || !event.me || event.me.status !== 'approved') return;
    if (!['lobby', 'live', 'finishing'].includes(event.phase)) return;
    const current = readActiveMission(userId);
    if (current?.missionId === event.mission_id && current?.event) return;
    setActiveMission({ missionId: event.mission_id, title: event.title_he || event.title_en,
      until: event.ends_at || null, userId, event: true });
  }, [event, userId]);

  // The board and the cards move with the heartbeat's score version.
  const version = live?.live?.score_version;
  useEffect(() => { if (version != null) loadCards(); }, [version, loadCards]);
  const phase = live?.live?.phase || event?.phase;
  useEffect(() => { if (phase && event && phase !== event.phase) load(); }, [phase]); // eslint-disable-line

  if (error) {
    return (
      <div style={es.page}>
        <p role="alert">{error === NOT_FOUND ? t('notFound') : error}</p>
        <BackToCamera t={t} navigate={navigate} />
      </div>
    );
  }
  if (!event) return <div style={es.page}><p>{t('loading')}</p></div>;

  const me = live?.live?.me || event.me;
  const consent = !!(live?.live?.me?.location_consent ?? event.me?.location_consent);
  const cardsById = Object.fromEntries(cards.map((c) => [c.card_id, c]));
  const mine = cards.filter((c) => c.mine);
  const next = live?.live?.me?.next_waypoint || null;

  const setConsent = (location) => api.post(`/events/${missionId}/consent`, { location })
    .then(() => { load(); live?.poke?.(); }).catch((e) => setError(errorText(e)));
  const leave = () => api.post(`/events/${missionId}/leave`).then(() => navigate('/field', { replace: true }))
    .catch((e) => setError(errorText(e)));

  return (
    <div style={es.page} data-testid="event-field" data-phase={phase}>
      <header style={es.header}>
        <button type="button" style={es.back} onClick={() => navigate('/field')} data-testid="event-field-camera">
          {t('camera')}
        </button>
        <h1 style={es.title} dir="auto">{event.title_he || event.title_en}</h1>
        <span style={es.phase} data-testid="event-field-phase">{t(`phase.${phase}`)}</span>
      </header>

      {me?.status === 'pending' && <p style={es.banner} role="status">{t('waitingApproval')}</p>}
      {phase === 'lobby' && me?.status === 'approved' && <p style={es.banner}>{t('lobbyText')}</p>}
      {phase === 'finishing' && <p style={es.banner}>{t('finishingText')}</p>}
      {guest && <p style={es.safety} role="note">{t('safety.neverEat')}</p>}

      {me?.status === 'approved' && ['lobby', 'live'].includes(phase) && (
        <label style={es.consent}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
            data-testid="event-field-consent" />
          <span>{t('consent')}</span>
        </label>
      )}

      <div role="tablist" aria-label={t('tabs')} style={es.tabs}>
        {TABS.map((name) => (
          <button key={name} type="button" role="tab" aria-selected={tab === name} style={es.tab(tab === name)}
            onClick={() => setTab(name)} data-testid={`event-tab-${name}`}>{t(`tab.${name}`)}</button>
        ))}
      </div>

      <div style={es.body}>
        {tab === 'map' && (
          <div style={{ height: '55vh', minHeight: 260 }}>
            <FieldMiniMap full fix={live?.fix} positions={live?.live?.positions || []} waypoint={next}
              teams={event.teams} zones={zones} testid="event-map" />
            <p data-testid="event-field-next" style={{ margin: '8px 0' }}>
              {next ? t('nextWaypoint', { label: next.label || t(`waypoint.${next.kind}`) })
                : me?.done ? t('routeDone') : t('noWaypoint')}
            </p>
          </div>
        )}
        {tab === 'map' && phase === 'live' && live?.lastArrival?.kind === 'waypoint' && (
          <RadiusScan key={live.lastArrival.waypoint_id} radius={live.lastArrival.search_radius_m} />
        )}
        {tab === 'map' && phase === 'live' && me?.status === 'approved' && can(user, 'isolation:perform') && (
          <IsolationPanel missionId={missionId} fix={live?.fix} isolation={live?.live?.isolation}
            finds={mine.filter((c) => c.sample_id)} />
        )}
        {tab === 'map' && ['lobby', 'live'].includes(phase) && me?.status === 'approved' && (
          <PrepareForEvent event={event} />
        )}
        {tab === 'board' && <EventBoard board={live?.board} me={userId} cardsById={cardsById} />}
        {tab === 'chat' && event.channel_id && (
          <div style={{ height: '60vh', minHeight: 300 }}>
            <Conversation channelId={event.channel_id} me={userId} outbox={outbox} />
          </div>
        )}
        {tab === 'chat' && !event.channel_id && <p>{t('chatAfterApproval')}</p>}
        {tab === 'finds' && (
          <div style={{ display: 'grid', gap: 10 }} data-testid="event-finds">
            {mine.length > 0 && <h2 style={es.h2}>{t('myFinds')}</h2>}
            {mine.map((c) => (
              <CollectionCard key={c.card_id} card={c}
                actions={['live', 'finishing'].includes(phase) && c.status !== 'withdrawn'
                  ? <EventFindAi card={c} onLinked={loadCards} /> : null} />
            ))}
            <h2 style={es.h2}>{t('allFinds')}</h2>
            {cards.length === 0 && <p>{t('noFinds')}</p>}
            {cards.filter((c) => !c.mine).map((c) => <CollectionCard key={c.card_id} card={c} compact />)}
          </div>
        )}
      </div>

      {me?.status && <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        <button type="button" style={{ ...es.leave, marginInlineEnd: 8 }} onClick={() => navigate(`/events/${missionId}/role`)}
          data-testid="event-field-role">{t('rolePage.mine')}</button>
        {me.role !== 'organiser' && !['finished', 'cancelled'].includes(phase) && (
          <button type="button" style={es.leave} onClick={leave} data-testid="event-field-leave">{t('leave')}</button>
        )}
      </div>}
    </div>
  );
}

function BackToCamera({ t, navigate }) {
  return <button type="button" style={es.back} onClick={() => navigate('/field')}>{t('camera')}</button>;
}

const es = {
  page: { minHeight: '100dvh', background: 'var(--c-page, #f7f7f2)', color: 'var(--c-text-fg, #111)', padding: 12,
    paddingTop: 'calc(env(safe-area-inset-top) + 12px)', boxSizing: 'border-box' },
  header: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
  back: { minHeight: 44, padding: '0 14px', borderRadius: 22, border: '1px solid var(--c-border)',
    background: 'var(--c-surface)', cursor: 'pointer', font: 'inherit' },
  title: { margin: 0, fontSize: 18, flex: 1, minWidth: 140 },
  phase: { fontSize: 12, padding: '4px 10px', borderRadius: 12, background: 'var(--c-primary-soft, #e8f5e9)' },
  banner: { padding: '8px 10px', borderRadius: 8, background: 'var(--c-surface)', border: '1px solid var(--c-border)' },
  safety: { fontSize: 13, background: '#fff3e0', color: '#5d4037', padding: '8px 10px', borderRadius: 8 },
  consent: { display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0', minHeight: 44 },
  tabs: { display: 'flex', gap: 4, borderBottom: '1px solid var(--c-border)', marginBottom: 8, overflowX: 'auto' },
  tab: (on) => ({ minHeight: 44, padding: '0 14px', border: 'none', borderBottom: on ? '3px solid var(--c-primary)' : '3px solid transparent',
    background: 'transparent', fontWeight: on ? 700 : 400, cursor: 'pointer', font: 'inherit' }),
  body: { minHeight: 200 },
  h2: { fontSize: 15, margin: '8px 0 0' },
  leave: { marginTop: 16, minHeight: 44, padding: '0 14px', borderRadius: 8, border: '1px solid var(--c-border)',
    background: 'transparent', cursor: 'pointer', font: 'inherit' },
};
